import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useState, useEffect, useRef, useCallback } from 'react';
import { ERROR_GEOLOCATION_NOT_SUPPORTED, GEOLOCATION_TIMEOUT_MS, PERMISSION_NAME_GEOLOCATION, PERMISSION_STATE_GRANTED, } from './constants';
export const GeoPositionContext = createContext(undefined);
export default function GeoPositionProvider({ children }) {
    const [position, setPosition] = useState(undefined);
    const [heading, setHeading] = useState(undefined);
    const [error, setError] = useState(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [isKnown, setIsKnown] = useState(false);
    const [isPermissionGranted, setIsPermissionGranted] = useState(false);
    const [isFollowing, setIsFollowing] = useState(false);
    const [repanFunction, setRepanFunction] = useState(undefined);
    const watchIdRef = useRef(undefined);
    const request = useCallback(() => {
        if (!navigator.geolocation) {
            setError(ERROR_GEOLOCATION_NOT_SUPPORTED);
            return;
        }
        if (watchIdRef.current !== undefined)
            return;
        setIsLoading(true);
        watchIdRef.current = navigator.geolocation.watchPosition((pos) => {
            setPosition({
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude,
            });
            setIsKnown(true);
            setError(undefined);
            setIsLoading(false);
            setHeading(pos.coords.heading ?? undefined);
        }, (err) => {
            setError(err.message);
            setIsLoading(false);
            // Only treat permission denial as a hard failure that invalidates the last known position.
            // Transient errors (POSITION_UNAVAILABLE, TIMEOUT) can fire during normal use; keep the
            // last fix so consumers don't flicker the user marker on/off.
            if (err.code === err.PERMISSION_DENIED) {
                setIsKnown(false);
                setHeading(undefined);
                setPosition(undefined);
                setIsFollowing(false);
            }
        }, { enableHighAccuracy: true, maximumAge: 0, timeout: GEOLOCATION_TIMEOUT_MS });
    }, []);
    useEffect(() => {
        return () => {
            if (watchIdRef.current !== undefined && navigator.geolocation) {
                navigator.geolocation.clearWatch(watchIdRef.current);
                watchIdRef.current = undefined;
            }
        };
    }, []);
    // Auto-request position only when permission is already granted (no browser prompt).
    // If state is 'prompt' or 'denied', let the app's UI handle requesting explicitly.
    useEffect(() => {
        let isMounted = true;
        let permissionStatus;
        let handlePermissionChange;
        if (navigator.permissions) {
            navigator.permissions.query({ name: PERMISSION_NAME_GEOLOCATION }).then((status) => {
                if (!isMounted)
                    return;
                permissionStatus = status;
                handlePermissionChange = () => {
                    if (!isMounted)
                        return;
                    setIsPermissionGranted(status.state === PERMISSION_STATE_GRANTED);
                    if (status.state === PERMISSION_STATE_GRANTED) {
                        request();
                    }
                };
                setIsPermissionGranted(status.state === PERMISSION_STATE_GRANTED);
                if (status.state === PERMISSION_STATE_GRANTED) {
                    request();
                }
                status.addEventListener('change', handlePermissionChange);
            });
        }
        return () => {
            isMounted = false;
            if (permissionStatus && handlePermissionChange) {
                permissionStatus.removeEventListener('change', handlePermissionChange);
            }
        };
    }, [request]);
    return (_jsx(GeoPositionContext.Provider, { value: {
            position,
            heading,
            error,
            isLoading,
            isKnown,
            isPermissionGranted,
            isFollowing,
            setIsFollowing,
            request,
            repanFunction,
            setRepanFunction,
        }, children: children }));
}
